// Hex field — a board of flat-top hexagon tiles (see hexGrid.js). Every tile
// has a plot type:
//   • GRASS → plain green (most tiles); ~28% get a decorative tree on top
//   • FARM  → rice paddy; cycles the four seasonal colors via the click queue
//   • HOME  → fixed red (the player's home tile)
//
// Tiles are keyed by their axial "q,r" string. The old square grid + For-Sale
// sign + per-tile purchase system is gone: tiles start owned (grass) and the
// player builds on them via the BUILD menu.

import * as THREE from 'three'
import gsap from 'gsap'
import { SeasonConfig, PlotConfig, PlotTypes, GrassConfig, RaidDamageConfig, HomeDioramaTile, FarmDioramaTile, HomeRecipe, FarmRecipe, GroundRecipe, WeedsRecipe } from '../config/balance.js'
import { registerPaddyRows, unregisterPaddyRows } from './paddyRows.js'
import { syntyReady, hasRecipe, buildRecipeGroup, buildGroundCover, getRecipe } from './synty.js'
import { Events, on } from '../ui/eventBus.js'
import { listAllTiles, getHomeTileIndex, getHomeLevel } from '../game/plotState.js'
import { getSeasonConfig } from '../game/seasonManager.js'
import {
  fieldCoords, axialToWorld, key, parseKey, createHexTileGeometry, createHexOutlineGeometry, TILE_HEIGHT, HEX_SIZE, GROUND_Y,
  FIELD_RADIUS, hexDistance, ringCoords, hash01,
} from './hexGrid.js'
import { buildSyntySurroundings } from './surroundings.js'
import * as Assets from '../assets/AssetRegistry.js'
import { registerDiorama, clearCollision } from './collision.js'
import { createCampfire } from './effects.js'

// Decorative trees pulled from the low-poly pack (Exteriors.glb). Picked
// deterministically per tile so the scenery is stable across reloads.
const TREE_NAMES = ['Tree_1', 'Tree_2', 'Tree_4', 'Tree_6', 'Oak_1', 'Cherry_Tree_1', 'Sakura', 'bamboo_set_1']

// Perimeter backdrop — hills (vertically exaggerated into mountains) + rocky
// crags ringed just OUTSIDE the playable field so the world reads as a valley
// hemmed by mountains instead of a flat island that drops into the void.
const HILL_NAMES = ['Hill', 'Hill001']
const CRAG_NAMES = ['Stone_1', 'Stone_2', 'Stone_3', 'Stone_4', 'Stone_5', 'Stone_6', 'Stone_7', 'Stone_8']

// Diorama placement API — set by createPaddy so other modules (buildings.js)
// can swap a tile's surface for its mapped Tile_N diorama when a structure is
// built there. Delegates into the createPaddy closure, which owns the scene
// group + collision map. No-ops harmlessly before the field has been built.
let _dioramaApi = null
// recipeId (optional) = a Synty hex recipe; used when the kit is loaded, else the Tile_N number is the fallback.
export function placeBuildingDiorama(tileKey, tileNumber, recipeId = null) {
  return _dioramaApi?.place(tileKey, tileNumber, recipeId) ?? null
}
export function removeBuildingDiorama(tileKey) {
  _dioramaApi?.remove(tileKey)
}
// Re-sample collision for every placed diorama (after a building is rotated or removed, its old footprint must stop blocking).
export function rearmDioramaCollision() {
  _dioramaApi?.rearm()
}

// World-space bbox of a diorama's GROUND hexagon — the single mesh with the
// largest XZ footprint that's relatively flat (the "base"). Using the base
// (which is identical across every Tile_N) rather than the full diorama bbox
// (which varies with trees/roofs) makes every tile scale + center IDENTICALLY,
// so they tessellate exactly with no gaps.
function groundBox(obj) {
  obj.updateMatrixWorld(true)
  const tmp = new THREE.Box3()
  let best = null
  let bestArea = -1
  obj.traverse((o) => {
    if (!o.isMesh) return
    tmp.setFromObject(o)
    const w = tmp.max.x - tmp.min.x
    const d = tmp.max.z - tmp.min.z
    const h = tmp.max.y - tmp.min.y
    const area = w * d
    // Ground = biggest XZ area AND flat (height well under its width).
    if (area > bestArea && h < Math.min(w, d) * 0.6) {
      bestArea = area
      best = tmp.clone()
    }
  })
  return best ?? new THREE.Box3().setFromObject(obj)
}

// Center a diorama on its ground hex + scale so the pointy-top base radius
// equals HEX_SIZE, then drop it on the (q,r) tile. Identical math for every
// tile → precise tessellation.
function placeDiorama(inner, q, r, parent) {
  const gb = groundBox(inner)
  const center = gb.getCenter(new THREE.Vector3())
  const size = gb.getSize(new THREE.Vector3())
  inner.position.x -= center.x
  inner.position.z -= center.z
  inner.position.y -= gb.min.y // base sits at y=0
  const wrap = new THREE.Group()
  wrap.add(inner)
  wrap.scale.setScalar((2 * HEX_SIZE) / (size.z || 1)) // pointy-top base depth = 2·r
  const { x, z } = axialToWorld(q, r)
  wrap.position.set(x, TILE_HEIGHT / 2, z)
  parent.add(wrap)
  // Sample this diorama's solid geometry into the collision map so characters
  // can't walk through its buildings/walls/trees — only along its open paths.
  registerDiorama(wrap)
  return wrap
}

export function createPaddy({ scene, rice = null }) { // rice: scene/riceField.js — draws the paddies' crop rows itself (instanced, growing with the clicks)
  const group = new THREE.Group()
  // Plain tiles are raised hex BLOCKS, not thin slabs — their top sits at the
  // same walkable height (GROUND_Y) as the village dioramas, so props + cats
  // sit on them properly instead of looking dropped on the bare floor. The top
  // is held a hair (BLOCK_GAP) under GROUND_Y so it tucks just beneath the
  // diorama bases on gallery tiles and never z-fights with them.
  const BLOCK_GAP = 0.02
  const BLOCK_H = GROUND_Y - BLOCK_GAP               // block height; bottom at y=0, top at GROUND_Y-gap
  const BLOCK_TOP = BLOCK_H                          // world Y of the tile top face
  const geometry = createHexTileGeometry(HEX_SIZE, BLOCK_H)
  // Outline traces the tile's TOP rim — bake its y at the block top (+ε).
  const outlineGeo = createHexOutlineGeometry(HEX_SIZE, BLOCK_TOP * 2)
  // 1px grid line drawn on each tile's top rim so individual hexes read — present but only just. (It is blended in linear
  // light, so against the dark night scene even a tiny opacity shows; keep it low.)
  const outlineMat = new THREE.LineBasicMaterial({ color: 0xb4c6a0, transparent: true, opacity: 0.04 })
  const tilesByKey = new Map() // "q,r" → tile mesh
  const tiles = []             // flat list (heroController raycasts this for click-to-move)

  // Build every field tile as a raised hex block + a thin top-rim outline.
  for (const { q, r } of fieldCoords()) {
    const k = key(q, r)
    const material = new THREE.MeshLambertMaterial({ color: GrassConfig.color })
    const tile = new THREE.Mesh(geometry, material)
    const { x, z } = axialToWorld(q, r)
    tile.position.set(x, BLOCK_H / 2, z) // sit on the floor (y=0), top at BLOCK_TOP
    tile.userData.tileIndex = k
    group.add(tile)
    const outline = new THREE.LineLoop(outlineGeo, outlineMat)
    outline.position.set(x, 0, z)
    outline.raycast = () => {} // never block click-to-move raycasts
    group.add(outline)
    tilesByKey.set(k, tile)
    tiles.push(tile)
  }

  scene.add(group)

  // Scatter trees on grass tiles (after tiles exist + plots are known).
  const treeGroup = new THREE.Group()
  group.add(treeGroup)
  function rebuildTrees() {
    // Clear old trees. DO NOT dispose geometry/material — getByName() clones
    // SHARE the master's BufferGeometry + material, so disposing here would
    // free the GPU buffers still used by every other clone (and the tile
    // dioramas), making them render as garbage and vanish. Just detach.
    for (let i = treeGroup.children.length - 1; i >= 0; i--) {
      const c = treeGroup.children[i]
      treeGroup.remove(c)
      c.traverse?.((o) => { if (o.isInstancedMesh) o.dispose?.() }) // instance buffers only; geometry is shared
    }
    // Synty kit loaded → plain tiles become undeveloped land (each hex gets its own scatter of bamboo + rocks).
    // WILD (unclaimed) tiles become a dense bamboo forest; claimed GRASS tiles become open undeveloped land.
    if (syntyReady() && hasRecipe(GroundRecipe) && hasRecipe(WeedsRecipe)) {
      const open = [], forest = []
      for (const { tileIndex, plot } of listAllTiles()) {
        const tile = tilesByKey.get(tileIndex); if (!tile) continue
        const t = { key: tileIndex, x: tile.position.x, y: GROUND_Y, z: tile.position.z }
        if (plot?.type === PlotTypes.GRASS) open.push(t)
        else if (plot?.type === PlotTypes.WILD) forest.push(t)
      }
      for (const [list, id] of [[open, GroundRecipe], [forest, WeedsRecipe]]) {
        const g = list.length ? buildGroundCover(list, id) : null
        if (g) treeGroup.add(g)
      }
      return
    }
    for (const { tileIndex, plot } of listAllTiles()) {
      if (plot?.type !== PlotTypes.GRASS) continue
      const roll = hash01(tileIndex)
      if (roll >= GrassConfig.treeChance) continue
      const name = TREE_NAMES[Math.floor(hash01(tileIndex + '#tree') * TREE_NAMES.length)]
      const raw = Assets.getByName(name)
      if (!raw) continue
      const tree = Assets.fit(raw, { footprint: HEX_SIZE * 1.0, anchorBase: true })
      const tile = tilesByKey.get(tileIndex)
      if (!tile) continue
      tree.position.set(tile.position.x, GROUND_Y, tile.position.z)
      treeGroup.add(tree)
    }
  }

  // Perimeter mountains — a backdrop ring of hills + crags just outside the
  // field. Hills are stretched vertically into mountains; everything grows
  // taller and is seated deeper the farther out it is, so the rim reads as a
  // rising mountain range that hides the island's drop-off. Purely decorative
  // (well outside the cats' radius-6 clamp).
  const mountainGroup = new THREE.Group()
  group.add(mountainGroup)

  // The land around the field (Synty kit) — built by scene/surroundings.js (a visited kingdom uses the same).
  function placeSyntySurroundings() {
    mountainGroup.add(buildSyntySurroundings())
    return true
  }
  function placePerimeterMountains() {
    for (let i = mountainGroup.children.length - 1; i >= 0; i--) {
      const c = mountainGroup.children[i]
      mountainGroup.remove(c)
      c.traverse?.((o) => { if (o.isInstancedMesh) o.dispose?.() })
    }
    if (syntyReady() && placeSyntySurroundings()) return
    for (const { q, r } of ringCoords(FIELD_RADIUS + 1, FIELD_RADIUS + 4)) {
      const tier = hexDistance({ q, r }, { q: 0, r: 0 }) - FIELD_RADIUS // 1 (inner ring) → 4 (outer)
      const k = key(q, r)
      const { x, z } = axialToWorld(q, r)
      // Mostly green hills (foothills); occasional grey crag for rocky variety.
      const useHill = hash01(k + '#mt') < 0.82
      const name = useHill
        ? HILL_NAMES[Math.floor(hash01(k + '#mh') * HILL_NAMES.length)]
        : CRAG_NAMES[Math.floor(hash01(k + '#mc') * CRAG_NAMES.length)]
      const raw = Assets.getByName(name)
      if (!raw) continue
      // Wide footprints (overlap into a continuous ridge) growing outward.
      const foot = HEX_SIZE * (2.6 + tier * 0.9 + hash01(k + '#mf') * 0.8)
      const m = Assets.fit(raw, { footprint: foot, anchorBase: true })
      // Vertical exaggeration: hills → mountains, taller toward the outside.
      m.scale.y *= 1.7 + tier * 0.9 + hash01(k + '#my') * 0.8
      // Small jitter only, so neighbors still merge into a wall (no big gaps).
      const jx = (hash01(k + '#mjx') - 0.5) * HEX_SIZE * 0.4
      const jz = (hash01(k + '#mjz') - 0.5) * HEX_SIZE * 0.4
      // Sink the base below the field (deeper outward) so slopes mask the cliff.
      m.position.set(x + jx, -1.2 - tier * 0.7, z + jz)
      m.rotation.y = hash01(k + '#mr') * Math.PI * 2
      mountainGroup.add(m)
    }
  }

  // ── Building dioramas ────────────────────────────────────────────────────
  // A built tile swaps its surface for the mapped Tile_N mini-scene (see
  // BuildingDioramaTile). The diorama drops ON TOP of the plain hex block (its
  // base tucks just above it, like the old gallery) so there's no seam to hide.
  // placeDiorama() also samples the diorama's solid geometry into the collision
  // map, so cats path around its walls/props instead of through them. A warm
  // night glow is re-seated over every built tile for the day/night system.
  const dioramaByTile = new Map() // tileKey → wrap group
  let homeDioramaKey = null
  const farmDioramaKeys = new Set() // tiles showing a rice-paddy diorama (CR-5)

  // Collision is sampled from a placed diorama's solid geometry. The rice paddy is walkable, so it is skipped.
  const armCollision = (wrap) => { if (wrap.userData.synty !== FarmRecipe) registerDiorama(wrap) }

  // Synty recipe → a wrap group sitting on the tile (ground slab top at GROUND_Y).
  function placeSyntyDiorama(recipeId, tileKey) {
    const { q, r } = parseKey(tileKey)
    const opts = {}
    if (recipeId === FarmRecipe) {
      const t = tilesByKey.get(tileKey)
      // The Synty paddy shows what a real paddy does each season: mud, then flooded water, then drained.
      opts.slabColor = getSeasonConfig()?.paddyColor ?? t?.material.color.getHex()
    }
    const wrap = buildRecipeGroup(recipeId, tileKey, opts)
    if (!wrap) return null
    const { x, z } = axialToWorld(q, r)
    wrap.position.set(x, GROUND_Y, z)
    group.add(wrap)
    armCollision(wrap)
    if (recipeId === FarmRecipe) {
      // The crop rows: handed to riceField (one instanced mesh for the whole field, each row growing with the season's clicks);
      // if that is not possible (the kit's row piece is missing) the tile keeps its own row meshes and the old whole-tile growth.
      const rowGroups = wrap.children.filter((c) => c.userData.piece === 'Env_CropRow')
      const rows = rowGroups.map((g) => ({ lx: g.position.x, lz: g.position.z }))
      if (rice && rowGroups.length && rice.registerPaddy(tileKey, wrap, rows)) {
        for (const g of rowGroups) wrap.remove(g)
        wrap.userData.instancedRows = true
      } else {
        registerPaddyRows(wrap, rows) // let characters walk OVER the crop rows instead of sinking into them
      }
    }
    return wrap
  }

  function placeBuildingDioramaImpl(tileKey, tileNumber, recipeId = null) {
    removeBuildingDioramaImpl(tileKey)
    if (tileNumber == null && !recipeId) return null
    const { q, r } = parseKey(tileKey)
    let wrap = null
    if (recipeId && syntyReady() && hasRecipe(recipeId)) wrap = placeSyntyDiorama(recipeId, tileKey)
    if (!wrap) {
      if (tileNumber == null) return null
      const inner = Assets.getByName(`Tile_${tileNumber}`)
      if (!inner) return null
      wrap = placeDiorama(inner, q, r, group)
    }
    // Drop-in pop so a freshly built diorama clearly lands on its tile.
    const s = wrap.scale.x
    wrap.userData.baseScale = s // canonical full scale (Layer 2 reads this, not the mid-pop value)
    wrap.scale.setScalar(s * 0.01)
    gsap.to(wrap.scale, { x: s, y: s, z: s, duration: 0.45, ease: 'back.out(1.8)' })
    dioramaByTile.set(tileKey, wrap)
    return wrap
  }

  function removeBuildingDioramaImpl(tileKey) {
    const wrap = dioramaByTile.get(tileKey)
    if (!wrap) return
    group.remove(wrap)
    if (wrap.userData.instancedRows) rice?.unregisterPaddy(tileKey)
    else unregisterPaddyRows(wrap)
    dioramaByTile.delete(tileKey)
  }

  // Re-seat the player's home manor (Tile_7) on the current kingdom's home tile.
  function placeHomeDiorama() {
    if (homeDioramaKey) { removeBuildingDioramaImpl(homeDioramaKey); homeDioramaKey = null }
    const hk = getHomeTileIndex()
    if (!hk) return
    // CR-5: render the home by its level — SHACK (default) or MIDDLE (manor).
    const lvl = getHomeLevel()
    placeBuildingDioramaImpl(hk, HomeDioramaTile[lvl] ?? HomeDioramaTile.SHACK, HomeRecipe[lvl] ?? HomeRecipe.SHACK)
    homeDioramaKey = hk
  }

  // CR-5: keep a rice-paddy diorama on every FARM tile so the field reads as a
  // paddy. Re-synced on boot + plot/kingdom changes (skips tiles already holding
  // a diorama, e.g. a granary built on a paddy).
  function placeFarmDioramas() {
    const farmSet = new Set(
      listAllTiles().filter((t) => t.plot?.type === PlotTypes.FARM).map((t) => t.tileIndex),
    )
    for (const tk of [...farmDioramaKeys]) {
      if (!farmSet.has(tk)) { removeBuildingDioramaImpl(tk); farmDioramaKeys.delete(tk) }
    }
    for (const tk of farmSet) {
      if (!dioramaByTile.has(tk)) { placeBuildingDioramaImpl(tk, FarmDioramaTile, FarmRecipe); farmDioramaKeys.add(tk) }
    }
    applyFarmGrowth(false) // new/just-placed paddies adopt the current season's height
  }

  // Layer 2: scale each rice-paddy diorama's HEIGHT to the season's plantScale so
  // the field visibly grows (short nubs → full → golden). State-based — one tween
  // per season change, no per-frame cost. wrap.scale.x holds the base uniform
  // scale; we only flex Y (the base is flat, so only the plants read the change).
  function applyFarmGrowth(animate) {
    const ps = getSeasonConfig()?.plantScale ?? 1
    for (const tk of farmDioramaKeys) {
      const wrap = dioramaByTile.get(tk)
      if (!wrap || wrap.userData.instancedRows) continue // (instanced rows grow with the clicks — riceField.js)
      const base = wrap.userData.baseScale ?? wrap.scale.x // full scale, NOT the mid-pop value
      // Season change → tween now. Fresh placement (animate=false) → wait out the
      // 0.45s spawn pop first so we never kill/fight its x/y/z tween (which would
      // leave the diorama collapsed). Only Y flexes; x/z stay at the base scale.
      gsap.to(wrap.scale, {
        y: base * ps,
        duration: animate ? 0.9 : 0.4,
        delay: animate ? 0 : 0.5,
        ease: 'power1.out',
      })
    }
  }

  _dioramaApi = {
    place: placeBuildingDioramaImpl,
    remove: removeBuildingDioramaImpl,
    rearm: () => { clearCollision(); for (const wrap of dioramaByTile.values()) armCollision(wrap) },
  }

  repaintAll()
  rebuildTrees()

  // Initial scenery pass. Buildings persisted in this kingdom are placed via
  // buildings.broadcast() (which fires BuildingAdded → buildings.spawn →
  // placeBuildingDiorama) BEFORE this timeout runs, so we re-arm collision for
  // those dioramas, then drop the home manor + the perimeter mountain backdrop.
  // (A short delay keeps the first frames cheap; the GLB master is already
  // loaded by this point, so the dioramas appear immediately.)
  setTimeout(() => {
    clearCollision()
    for (const wrap of dioramaByTile.values()) armCollision(wrap) // re-arm built tiles
    placeHomeDiorama()
    placeFarmDioramas()
    placePerimeterMountains()
    // A couple of village campfires near the home tile (crossed logs + voxel
    // flame + embers + smoke + warm light pool). Lit only after dark.
    const hk = getHomeTileIndex()
    if (hk) {
      const { q, r } = parseKey(hk)
      const seeds = ringCoords(1, 2).slice(0, 2).map((c) => ({ q: q + c.q, r: r + c.r }))
      for (const c of seeds) {
        const { x, z } = axialToWorld(c.q, c.r)
        const ang = hash01(key(c.q, c.r) + '#fire') * Math.PI * 2
        const off = HEX_SIZE * 0.55
        createCampfire({
          scene,
          position: new THREE.Vector3(x + Math.cos(ang) * off, GROUND_Y, z + Math.sin(ang) * off),
          scale: 0.85,
        })
      }
    }
  }, 1800)

  on(Events.PlotChanged, () => { repaintAll(); rebuildTrees(); placeFarmDioramas() })
  on(Events.HomeLevelChange, () => placeHomeDiorama()) // shack→manor swaps in place
  on(Events.KingdomChange, () => {
    // New kingdom: wipe collision, re-place this kingdom's home manor + the
    // mountain ring. Old building dioramas are torn down by buildings.clearAll;
    // new ones re-register their collision when buildings.broadcast() re-fires.
    clearCollision()
    farmDioramaKeys.clear() // dioramaByTile is rebuilt per kingdom; drop stale paddy keys
    placeHomeDiorama() // tears down the previous kingdom's home, places the new one
    placeFarmDioramas()
    placePerimeterMountains()
    repaintAll()
    rebuildTrees()
  })
  on(Events.PlotDamaged, ({ tileIndex }) => {
    const tile = tilesByKey.get(tileIndex)
    if (tile) tile.material.color.setHex(RaidDamageConfig.scorchedTileColor)
    dioramaByTile.get(tileIndex)?.userData.slabMat?.color.setHex(RaidDamageConfig.scorchedTileColor)
    rice?.setBurned(tileIndex, true) // the rows char too
  })
  on(Events.RepairComplete, () => { // repaired paddies: rows and water back to the season's look
    rice?.clearBurns()
    repaintAll()
    for (const tk of farmDioramaKeys) { const slab = dioramaByTile.get(tk)?.userData.slabMat; if (slab) slab.color.setHex(getSeasonConfig()?.paddyColor ?? GrassConfig.color) }
  })

  function repaintAll() {
    for (const { tileIndex, plot } of listAllTiles()) {
      const tile = tilesByKey.get(tileIndex)
      if (!tile) continue
      const color = PlotConfig[plot?.type]?.color ?? GrassConfig.color
      tile.material.color.setHex(color)
    }
  }

  // CR-2: seasons are time-boxed. Each rice paddy tweens to the new season's
  // palette colour on change (brown→green→gold…), so the field always reads as
  // a living rice paddy regardless of how many tiles you own.
  on(Events.SeasonChange, ({ config, sourceColor, previous }) => {
    const target = new THREE.Color(config.tileColor)
    for (const { tileIndex, plot } of listAllTiles()) {
      if (plot?.type !== PlotTypes.FARM) continue
      const tile = tilesByKey.get(tileIndex)
      if (!tile) continue
      tile.material.color.setHex(sourceColor)
      gsap.to(tile.material.color, { r: target.r, g: target.g, b: target.b, duration: 0.8, ease: 'power1.out' })
      const slab = dioramaByTile.get(tileIndex)?.userData.slabMat // Synty paddy: tint its water/soil slab too
      if (slab) { // water / mud / drained ground, per season
        const to = new THREE.Color(config.paddyColor ?? config.tileColor)
        slab.color.setHex(SeasonConfig[previous]?.paddyColor ?? sourceColor)
        gsap.to(slab.color, { r: to.r, g: to.g, b: to.b, duration: 0.8, ease: 'power1.out' })
      }
    }
    applyFarmGrowth(true) // grow/shrink the rice to match the new season
  })

  return { group, tiles }
}

